import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  costFormater,
  Heading,
  InvestmentPathCard,
  InvestmentPathType,
  Text,
} from '@common-fe/common-fe';

import { BuySellOptions, INVESTMENTS_ERROR_KEY } from '@/common/constants';
import ROUTES from '@/common/routes';
import Modal from '@/components/elements/AutoBlockedModal/Modal';
import { useHistory } from '@/modules/core/hooks';
import useBuySellStore from '@/modules/investments/BuySell/stores/useBuySell.store';
import { useMyEnrollments } from '@/modules/transaction';

import { useInvestor } from '../hooks';
interface Props {
  accountId?: string;
  isChangeModel?: boolean;
  isSelfDirectedMode?: boolean;
  visible: boolean;
  onSetVisible: (visible: boolean) => void;
}
const FILTERED_OPTIONS = [InvestmentPathType.BROKERAGE];

const InvestmentReactivationModal: React.FC<Props> = ({
  isSelfDirectedMode,
  accountId,
  visible,
  onSetVisible,
  isChangeModel,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { data: investor } = useInvestor(accountId);
  const [isSubmitted, setSubmitted] = useState(false);
  const { setBuySellTabState } = useBuySellStore();
  const { currentMyAccount } = useMyEnrollments(accountId);
  const currentOptions = useMemo(() => {
    return currentMyAccount?.investmentOptionTypes?.filter((key) => !FILTERED_OPTIONS.includes(key)) || [];
  }, [currentMyAccount?.investmentOptionTypes]);
  const [currentOption, setCurrentOption] = useState<InvestmentPathType>(
    isChangeModel && currentOptions.length > 1
      ? isSelfDirectedMode
        ? InvestmentPathType.MANAGED
        : InvestmentPathType.SELF_DIRECTED
      : isSelfDirectedMode
      ? InvestmentPathType.SELF_DIRECTED
      : InvestmentPathType.MANAGED
  );

  const currentError = useMemo(() => {
    if (
      currentMyAccount?.minimumInvestmentAmount &&
      currentMyAccount?.minimumInvestmentAmount >= investor?.availableToInvest
    ) {
      return `${t(INVESTMENTS_ERROR_KEY.MINIMUM_HSA_BALANCE)} ${costFormater(
        currentMyAccount.minimumInvestmentAmount
      )}`;
    }
    return '';
  }, [currentMyAccount?.minimumInvestmentAmount, investor?.availableToInvest, t]);
  const handleRedirect = useCallback(() => {
    setSubmitted(true);
    if (currentError) {
      return;
    }
    onSetVisible(false);
    window.scrollTo({ top: 0 });
    if (isSelfDirectedMode) {
      if (accountId && currentOption === InvestmentPathType.MANAGED) {
        history.push(ROUTES.MANAGED_SWITCH_EMPTY_QUESTIONNAIRE(accountId));
      } else {
        history.push(ROUTES.SELF_DIRECTED_INVEST_MORE(accountId));
      }
    } else {
      if (accountId && currentOption === InvestmentPathType.SELF_DIRECTED) {
        history.push(ROUTES.SELF_DIRECTED_EMPTY_SWITCH_QUESTIONNAIRE(accountId));
      } else {
        setBuySellTabState(BuySellOptions.INVEST_MORE_FUNDS);
        history.push(`${ROUTES.BUY_SELL}?id=${accountId}`);
      }
    }
  }, [accountId, currentError, currentOption, history, isSelfDirectedMode, onSetVisible, setBuySellTabState]);
  const renderPreviousSelected = useCallback(
    (planType: InvestmentPathType) => {
      if (isSelfDirectedMode) {
        if (planType !== InvestmentPathType.SELF_DIRECTED) {
          return null;
        }
      } else {
        if (planType !== InvestmentPathType.MANAGED) {
          return null;
        }
      }
      return (
        <Box margin={{ top: 's' }}>
          <Text size="medium" weight={700}>
            Previously Selected
          </Text>
        </Box>
      );
    },
    [isSelfDirectedMode]
  );

  return (
    <Modal visible={visible} wrapperStyle={{ zIndex: 999 }} onSetVisible={onSetVisible}>
      <Box data-testid="investment-switch-model_modal" direction="column" align="center">
        <Box margin={{ bottom: 's' }}>
          <Heading level={4} size="medium">
            {t('Choose Investment model')}
          </Heading>
        </Box>

        <Box pad="l" direction="row" background="module" round="moduleRound">
          {currentOptions.map((option) => {
            return (
              <Box
                key={option}
                data-testId={`investment-switch-model_option_${option}`}
                margin={{ right: 's' }}
              >
                <InvestmentPathCard
                  key={option}
                  testId={option}
                  type={option}
                  commingSoon={false}
                  checked={currentOption === option}
                  onClick={() => setCurrentOption(option)}
                >
                  {renderPreviousSelected(option)}
                </InvestmentPathCard>
              </Box>
            );
          })}
        </Box>
        {currentError && isSubmitted && (
          <Box align="end" width="100%" margin={{ top: 'l' }}>
            <Text size="medium" color="danger" style={{ lineHeight: '20px' }}>
              {t(currentError)}
            </Text>
          </Box>
        )}
        <Box fill direction="row" justify="end" align="end" margin={{ top: 'l' }}>
          <AppButton
            onClick={() => onSetVisible(false)}
            testId="investment-switch-model_cancel"
            width="140px"
            buttonType="secondary"
          >
            {t('Cancel')}
          </AppButton>
          <Box margin={{ left: 's' }}>
            <AppButton
              onClick={handleRedirect}
              disabled={!!currentError && isSubmitted}
              testId="investment-switch-model_switch"
              width="140px"
            >
              {t('Confirm')}
            </AppButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
export default InvestmentReactivationModal;
